import { useEffect, useState } from 'react'
import { Search, MessageSquare, Eye, EyeOff, Trash2, ShieldAlert, BarChart3, Clock, Check, XCircle, FileText } from 'lucide-react'
import { contentApi, type AdminContentComment } from '../../lib/api'

const STATUSES: { value: '' | 'pending' | 'visible' | 'hidden'; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'pending', label: '待审核' },
  { value: 'visible', label: '正常' },
  { value: 'hidden', label: '已隐藏/驳回' },
]

const CommentsManage = () => {
  const [items, setItems] = useState<AdminContentComment[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<{ total: number; visible: number; hidden: number; pending: number; today_new: number } | null>(null)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [titleKeyword, setTitleKeyword] = useState('')
  const [status, setStatus] = useState<'' | 'pending' | 'visible' | 'hidden'>('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null)
  const [preview, setPreview] = useState<AdminContentComment | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const r = await contentApi.adminListComments({
        keyword: keyword || undefined,
        content_title: titleKeyword || undefined,
        status: status || undefined,
        page,
        page_size: pageSize,
      })
      setItems(r.items || [])
      setTotal(r.total || 0)
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '加载失败' })
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const s = await contentApi.adminCommentStats()
      setStats(s)
    } catch { /* silent */ }
  }

  useEffect(() => { load(); loadStats() }, [page, status])

  const onHide = async (id: string) => {
    try {
      await contentApi.adminHideComment(id)
      setToast({ kind: 'success', msg: '已隐藏' })
      load(); loadStats()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' })
    }
  }

  const onApprove = async (id: string) => {
    try {
      await contentApi.adminApproveComment(id)
      setToast({ kind: 'success', msg: '已通过审核，评论公开展示' })
      load(); loadStats()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' })
    }
  }

  const onReject = async (id: string) => {
    if (!confirm('确认驳回该评论？驳回后不会公开展示。')) return
    try {
      await contentApi.adminRejectComment(id)
      setToast({ kind: 'success', msg: '已驳回' })
      load(); loadStats()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' })
    }
  }

  const onRestore = async (id: string) => {
    try {
      await contentApi.adminRestoreComment(id)
      setToast({ kind: 'success', msg: '已恢复' })
      load(); loadStats()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '操作失败' })
    }
  }

  const onDelete = async (id: string) => {
    if (!confirm('确认彻底删除该评论？此操作不可恢复。')) return
    try {
      await contentApi.adminDeleteComment(id)
      setToast({ kind: 'success', msg: '已删除' })
      load(); loadStats()
    } catch (e: any) {
      setToast({ kind: 'error', msg: e?.message || '删除失败' })
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {toast && (
        <div className={'fixed top-20 right-6 z-50 px-4 py-2 rounded shadow-lg text-sm ' + (toast.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200')}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600">×</button>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-blue-500" />评论管理
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">管理用户对健康科普内容的评论</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard icon={<BarChart3 className="w-4 h-4 text-blue-500" />} label="评论总数" value={stats.total} />
          <StatCard icon={<Clock className="w-4 h-4 text-amber-500" />} label="待审核" value={stats.pending} highlight={stats.pending > 0} />
          <StatCard icon={<Eye className="w-4 h-4 text-green-500" />} label="正常显示" value={stats.visible} />
          <StatCard icon={<EyeOff className="w-4 h-4 text-yellow-500" />} label="已隐藏/驳回" value={stats.hidden} />
          <StatCard icon={<MessageSquare className="w-4 h-4 text-orange-500" />} label="今日新增" value={stats.today_new} />
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load() } }}
              placeholder="搜索评论内容"
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-1">
            <FileText className="w-4 h-4 text-gray-400" />
            <input
              value={titleKeyword}
              onChange={e => setTitleKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load() } }}
              placeholder="搜索文章标题"
              className="border rounded px-2 py-1 text-sm w-40"
            />
          </div>
          <select value={status} onChange={e => { setStatus(e.target.value as any); setPage(1) }} className="border rounded px-2 py-1 text-sm">
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={() => { setPage(1); load() }} className="px-2 py-1 text-xs border rounded hover:bg-gray-50">搜索</button>
          <span className="text-xs text-gray-400 ml-auto">共 {total} 条</span>
        </div>

        {loading ? (
          <div className="text-center text-sm text-gray-400 py-6">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-6">暂无评论</div>
        ) : (
          <div className="space-y-2">
            {items.map(c => (
              <div key={c.id} className="border-b last:border-b-0 py-3 hover:bg-gray-50 px-2 rounded">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white flex items-center justify-center text-xs flex-shrink-0">
                    {(c.user_name || c.user_phone || '匿').slice(-2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                      <span className="font-medium text-gray-700">{c.user_name || c.user_phone || '匿名用户'}</span>
                      <span>{c.created_at?.slice(0, 16).replace('T', ' ')}</span>
                      <span className="text-gray-400">来自《{c.content_title || '-'}》</span>
                      <span className={'inline-block text-xs px-2 py-0.5 rounded ' + (
                        c.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        c.status === 'hidden' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                      )}>
                        {c.status === 'pending' ? '待审核' : c.status === 'hidden' ? '已隐藏/驳回' : '正常'}
                      </span>
                    </div>
                    <div className={'text-sm mt-1 break-words whitespace-pre-wrap ' + (c.status === 'hidden' ? 'text-gray-400 italic' : 'text-gray-800')}>
                      {c.body}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs">
                      <span className="text-gray-400">👍 {c.like_count}</span>
                      {c.ip_address && <span className="text-gray-400">IP: {c.ip_address}</span>}
                      <button onClick={() => setPreview(c)} className="text-blue-600 hover:underline">查看</button>
                      {c.status === 'pending' && (
                        <>
                          <button onClick={() => onApprove(c.id)} className="flex items-center gap-0.5 text-green-600 hover:underline font-medium">
                            <Check className="w-3.5 h-3.5" />通过
                          </button>
                          <button onClick={() => onReject(c.id)} className="flex items-center gap-0.5 text-orange-600 hover:underline">
                            <XCircle className="w-3.5 h-3.5" />驳回
                          </button>
                        </>
                      )}
                      {c.status === 'visible' && (
                        <button onClick={() => onHide(c.id)} className="text-yellow-600 hover:underline">隐藏</button>
                      )}
                      {c.status === 'hidden' && (
                        <button onClick={() => onRestore(c.id)} className="text-green-600 hover:underline">恢复显示</button>
                      )}
                      <button onClick={() => onDelete(c.id)} className="text-red-600 hover:underline">删除</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
            <span>第 {page} / {totalPages} 页</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">上一页</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">下一页</button>
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div onClick={() => setPreview(null)} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-medium">评论详情</h3>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="text-sm space-y-2">
              <div><span className="text-gray-500">用户：</span>{preview.user_name || preview.user_phone || '匿名用户'}</div>
              <div><span className="text-gray-500">内容：</span>{preview.content_title || '-'}</div>
              <div><span className="text-gray-500">IP：</span>{preview.ip_address || '-'}</div>
              <div><span className="text-gray-500">时间：</span>{preview.created_at}</div>
              <div className="bg-gray-50 rounded p-3 text-sm whitespace-pre-wrap">{preview.body}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const StatCard = ({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: number; highlight?: boolean }) => (
  <div className={'bg-white rounded-2xl shadow-md p-4 flex items-center gap-3' + (highlight ? ' ring-1 ring-amber-300' : '')}>
    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center">{icon}</div>
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  </div>
)

export default CommentsManage
